import { useState, useEffect } from "react";
import { Helmet } from 'react-helmet-async';
import { HelpCircle, X, ChevronDown } from "lucide-react";
import { getBackendUrl } from "../utils/api";
import ToolPageHeader from '../components/ToolPageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import StreamingAnalytics from '../components/StreamingAnalytics';

// SEO Configuration for AI Search Engines
const SEO_CONFIG = {
  title: 'Radio Listener Calculator | Audience Estimation Tool - Radio Solutions',
  description: 'Calculate your radio station audience size with our advanced listener estimation tool. Convert streaming data to actual listener counts using statistical modeling and population projections.',
  keywords: 'radio listener calculator, audience estimation tool, radio audience measurement, υπολογιστής ακροατών ραδιοφώνου, μέτρηση ακροατηρίου, streaming to listeners, radio reach calculator, audience analytics, listener count estimation, radio metrics, ακροαματικότητα ραδιοφώνου, radio station analytics',
  ogUrl: 'https://radio-apps.online/listener-calculator'
};

const ListenerCalculator = () => {
  const backendUrl = getBackendUrl();
  const { t, currentLang } = useLanguage();
  const [showHelp, setShowHelp] = useState(false);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Helper to split comma-separated translation strings
  const splitItems = (key) => t(key).split(',');

  // Metrics data with translations
  const metrics = [
    {
      title: t('sampleTitle'),
      desc: t('sampleDesc'),
      tip: t('sampleTip')
    },
    {
      title: t('positiveTitle'),
      desc: t('positiveDesc'),
      tip: t('positiveTip')
    },
    {
      title: t('populationTitle'),
      desc: t('populationDesc'),
      tip: t('populationTip')
    },
    {
      title: t('percentageTitle'),
      desc: t('percentageDesc'),
      tip: t('percentageTip')
    },
    {
      title: t('estimatedListenersTitle'),
      desc: t('estimatedListenersDesc'),
      tip: t('estimatedListenersTip'),
      highlight: true
    },
    {
      title: t('ciTitle'),
      desc: t('ciDesc'),
      tip: t('ciTip')
    },
    {
      title: t('fpcTitle'),
      desc: t('fpcDesc'),
      tip: t('fpcTip')
    },
    {
      title: t('trendTitle'),
      desc: t('trendDesc'),
      tip: t('trendTip')
    }
  ];

  // Benefits data with translations
  const benefits = [
    {
      icon: '💰',
      title: t('costReduction'),
      items: splitItems('costReductionItems')
    },
    {
      icon: '📈',
      title: t('audienceIncrease'),
      items: splitItems('audienceIncreaseItems')
    },
    {
      icon: '🎵',
      title: t('playlistAdaptation'),
      items: splitItems('playlistAdaptationItems'),
      note: t('playlistAdaptationNote')
    },
    {
      icon: '📊',
      title: t('proDataTitle'),
      items: splitItems('proDataItems')
    }
  ];

  return (
    <div style={{minHeight: '100vh', background: 'linear-gradient(180deg, #1A0730 0%, #2D0C4B 60%)'}}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{SEO_CONFIG.title}</title>
        <meta name="description" content={SEO_CONFIG.description} />
        <meta name="keywords" content={SEO_CONFIG.keywords} />
        <meta property="og:title" content={SEO_CONFIG.title} />
        <meta property="og:description" content={SEO_CONFIG.description} />
        <meta property="og:url" content={SEO_CONFIG.ogUrl} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={SEO_CONFIG.ogUrl} />
      </Helmet>
      
      {/* Unified Header */}
      <ToolPageHeader 
        title={t('listenerCalculatorTitle')} 
        showHelp={true}
        onHelpClick={() => setShowHelp(true)}
      />

      {/* Iframe with standalone HTML - pass language parameter */}
      <iframe
        src={`${backendUrl}/api/listener-calculator-widget?lang=${currentLang}`}
        style={{
          width: '100%',
          height: 'calc(100vh - 80px)',
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 8px 24px rgba(45,12,75,0.6)'
        }}
        title={t('listenerCalcTitle')}
      />

      {/* Help Modal */}
      {showHelp && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowHelp(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(180deg, #1A0730, #2D0C4B)',
              borderRadius: '16px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '85vh',
              overflow: 'auto',
              padding: '0',
              boxShadow: '0 25px 50px rgba(0,0,0,0.5)',
              border: '1px solid rgba(139,92,246,0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{
              background: 'linear-gradient(90deg, #6B3FA0, #8A4FDB)',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderRadius: '16px 16px 0 0'
            }}>
              <h2 style={{margin: 0, color: '#fff', fontSize: '1.5rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '10px'}}>
                <HelpCircle size={28} />
                {t('listenerCalcGuide')}
              </h2>
              <button
                onClick={() => setShowHelp(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: '#fff'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div style={{padding: '24px', color: '#e2e8f0'}}>
              
              {/* Section 1 */}
              <div style={{marginBottom: '32px'}}>
                <h3 style={{
                  color: '#2dd4bf',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    background: '#2dd4bf',
                    color: '#022',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    fontWeight: '800'
                  }}>1</span>
                  {t('listenerCalcWhat')}
                </h3>
                <p style={{lineHeight: '1.7', marginBottom: '16px'}}>
                  {t('listenerCalcWhatDesc')}
                </p>
                <ul style={{listStyle: 'none', padding: 0, margin: '0 0 16px 0'}}>
                  {splitItems('listenerCalcItems').map((item, i) => (
                    <li key={i} style={{padding: '8px 0', paddingLeft: '24px', position: 'relative'}}>
                      <span style={{position: 'absolute', left: 0, color: '#2dd4bf'}}>✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <div style={{
                  background: 'rgba(96,165,250,0.1)',
                  borderLeft: '4px solid #60a5fa',
                  padding: '16px',
                  borderRadius: '0 8px 8px 0',
                  marginBottom: '16px'
                }}>
                  <p style={{margin: 0, lineHeight: '1.7'}}>
                    <strong>{t('listenerCalcNote')}</strong><br/>
                    {t('listenerCalcStatistical')}
                  </p>
                </div>
                <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                  {splitItems('listenerCalcMetrics').map((item, i) => (
                    <li key={i} style={{padding: '6px 0', paddingLeft: '24px', position: 'relative'}}>
                      <span style={{position: 'absolute', left: 0, color: '#a855f7'}}>→</span>
                      {item}
                    </li>
                  ))}
                </ul>
                <p style={{marginTop: '16px', fontStyle: 'italic', color: '#94a3b8'}}>
                  {t('listenerCalcTool')}
                </p>
              </div>

              {/* Section 2 */}
              <div style={{marginBottom: '32px'}}>
                <h3 style={{
                  color: '#2dd4bf',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    background: '#2dd4bf',
                    color: '#022',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    fontWeight: '800'
                  }}>2</span>
                  {t('listenerCalcAnalysis')}
                </h3>

                <div style={{display: 'grid', gap: '16px'}}>
                  {/* Metric Cards */}
                  {metrics.map((metric, i) => (
                    <div key={i} style={{
                      background: metric.highlight ? 'rgba(168,85,247,0.15)' : 'rgba(30,41,59,0.8)',
                      border: metric.highlight ? '1px solid rgba(168,85,247,0.4)' : '1px solid rgba(71,85,105,0.3)',
                      borderRadius: '12px',
                      padding: '16px'
                    }}>
                      <h4 style={{
                        color: metric.highlight ? '#a855f7' : '#60a5fa',
                        margin: '0 0 8px 0',
                        fontSize: '1rem',
                        fontWeight: '600'
                      }}>{metric.title}</h4>
                      <p style={{margin: '0 0 8px 0', fontSize: '0.95rem', color: '#cbd5e1'}}>{metric.desc}</p>
                      <p style={{
                        margin: 0,
                        fontSize: '0.9rem',
                        color: '#2dd4bf',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '6px'
                      }}>
                        <span>➡</span> {metric.tip}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 3 */}
              <div>
                <h3 style={{
                  color: '#2dd4bf',
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span style={{
                    background: '#2dd4bf',
                    color: '#022',
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.9rem',
                    fontWeight: '800'
                  }}>3</span>
                  {t('howHelpsTitle')}
                </h3>

                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px'}}>
                  {benefits.map((benefit, i) => (
                    <div key={i} style={{
                      background: 'linear-gradient(135deg, rgba(45,212,191,0.1), rgba(96,165,250,0.1))',
                      border: '1px solid rgba(45,212,191,0.2)',
                      borderRadius: '12px',
                      padding: '20px'
                    }}>
                      <h4 style={{
                        color: '#fff',
                        margin: '0 0 12px 0',
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <span style={{fontSize: '1.5rem'}}>{benefit.icon}</span>
                        {benefit.title}
                      </h4>
                      <ul style={{margin: 0, paddingLeft: '20px'}}>
                        {benefit.items.map((item, j) => (
                          <li key={j} style={{color: '#cbd5e1', marginBottom: '6px', fontSize: '0.95rem'}}>{item}</li>
                        ))}
                      </ul>
                      {benefit.note && (
                        <p style={{
                          margin: '12px 0 0 0',
                          fontSize: '0.9rem',
                          color: '#2dd4bf',
                          fontStyle: 'italic'
                        }}>➡ {benefit.note}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

            </div>

            {/* Footer */}
            <div style={{
              borderTop: '1px solid rgba(71,85,105,0.3)',
              padding: '16px 24px',
              textAlign: 'center'
            }}>
              <button
                onClick={() => setShowHelp(false)}
                style={{
                  background: 'linear-gradient(90deg, #2dd4bf, #60a5fa)',
                  color: '#022',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '12px 32px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('understoodClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Streaming Analytics Section */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500 mb-2">
            {t('streamingAnalyticsTitle') || 'Streaming Analytics'}
          </h2>
          <p className="text-gray-400">
            {t('streamingAnalyticsDesc') || 'Ανεβάστε το CSV από τον streaming provider για λεπτομερή ανάλυση ακροαματικότητας'}
          </p>
        </div>
        <StreamingAnalytics />
      </div>
    </div>
  );
};

export default ListenerCalculator;
